import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AtlasCaseValidationService } from '@secca/core/services/atlas/atlas-case-validation.service';
import { DialogBoundryService } from '@secca/core/services/dialog-boundry.service';
import { formatAtlasRequestErrorMessage } from '@secca/shared/helpers/atlas/atlas-errorhandler-helper';
import { CaseCheck, CaseValidationRequest } from '@secca/shared/models/atlas/atlas-case-validation.model';
import { firstValueFrom } from 'rxjs';

export interface AtlasCaseValidationDialogData {
  caseNumber?: string | null;
  request: Partial<CaseValidationRequest>;
  fields: {
    insurancePeriodFrom: string | null;
    insurancePeriodTo: string | null;
    plannedDepartureDate: string | null;
    incidentDate: string | null;
  };
}

@Component({
  selector: 'app-atlas-case-validation-dialog',
  templateUrl: './atlas-case-validation-dialog.component.html',
  styleUrls: ['./atlas-case-validation-dialog.component.scss'],
  standalone: false
})
export class AtlasCaseValidationDialogComponent implements OnInit {
  loading = false;
  error: string | null = null;
  checks: CaseCheck[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AtlasCaseValidationDialogData,
    private dialogRef: MatDialogRef<AtlasCaseValidationDialogComponent>,
    private atlasCaseValidationService: AtlasCaseValidationService,
    public dialogBoundryService: DialogBoundryService
  ) {}

  ngOnInit(): void {
    void this.loadValidation();
  }

  close(): void {
    this.dialogRef.close();
  }

  get travelChecks(): CaseCheck[] {
    return this.getChecksByType('travel');
  }

  get otherSections(): Array<{ type: string; checks: CaseCheck[] }> {
    const sections = new Map<string, CaseCheck[]>();

    this.checks.forEach(check => {
      const normalizedType = this.normalizeType(check?.type);
      if (normalizedType === 'travel') {
        return;
      }

      if (!sections.has(normalizedType)) {
        sections.set(normalizedType, []);
      }
      sections.get(normalizedType)?.push(check);
    });

    return Array.from(sections.entries()).map(([type, checks]) => ({ type, checks }));
  }

  trackByCheck(index: number, check: CaseCheck): string {
    return `${check?.type || 'check'}-${check?.field || index}`;
  }

  getColorClass(color: string | null | undefined): string {
    const normalizedColor = (color || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-');
    return normalizedColor ? `check-color-${normalizedColor}` : 'check-color-neutral';
  }

  getSectionTitle(type: string): string {
    if (!type) {
      return 'Other';
    }

    return type
      .split(/[_\-\s]+/)
      .filter(Boolean)
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  hasCompleteRequest(): boolean {
    return this.getMissingFields().length === 0;
  }

  getMissingFieldsWarning(): string {
    const missingFields = this.getMissingFields();
    if (!missingFields.length) {
      return '';
    }

    return `Unable to validate case. Missing: ${missingFields.join(', ')}.`;
  }

  private async loadValidation(): Promise<void> {
    if (!this.hasCompleteRequest()) {
      this.error = this.getMissingFieldsWarning();
      this.checks = [];
      return;
    }

    this.loading = true;
    this.error = null;

    try {
      this.checks = await firstValueFrom(
        this.atlasCaseValidationService.validateCase(this.data.request as CaseValidationRequest)
      );
    } catch (error) {
      this.error = this.getRequestErrorMessage(error);
      this.checks = [];
    } finally {
      this.loading = false;
    }
  }

  private getChecksByType(type: string): CaseCheck[] {
    const normalizedType = this.normalizeType(type);
    return this.checks.filter(check => this.normalizeType(check?.type) === normalizedType);
  }

  private normalizeType(type: string | null | undefined): string {
    return (type || 'other').trim().toLowerCase();
  }

  private getRequestErrorMessage(error: unknown): string {
    return formatAtlasRequestErrorMessage(error, {
      fallbackMessage: 'Unable to load case validation.',
      statusMessage: 'Case validation failed'
    });
  }

  private getMissingFields(): string[] {
    const request = this.data?.request;
    if (!request) {
      return ['case validation request'];
    }

    const missingFields: string[] = [];

    if (!request.insurance_period_from) {
      missingFields.push('Insurance period from');
    }
    if (!request.insurance_period_to) {
      missingFields.push('Insurance period to');
    }
    if (!request.planned_departure_date) {
      missingFields.push('Planned departure date');
    }
    if (!request.incident_date) {
      missingFields.push('Incident date');
    }
    if (!request.incident_location) {
      missingFields.push('Incident location');
    }
    if (!Array.isArray(request.purposes_of_travel) || request.purposes_of_travel.length === 0) {
      missingFields.push('Purposes of travel');
    }

    return missingFields;
  }
}
